import type { AgentTool } from '@lumapps/agent-hub/types';
import { AIMessageRichTextValue } from '@lumapps/ask-ai-answer-rich-text/types';
import { AskAiConcept } from '@lumapps/ask-ai-concepts/types';
import type { AskAiSource } from '@lumapps/ask-ai-sources/types';
import { ACTIONABLE_ITEM_TYPES, ActionableItemPayload } from '@lumapps/ask-ai/types';
import { Group } from '@lumapps/groups/types';
import { TranslatableObject } from '@lumapps/translations';
import { MinimumViableUser, User } from '@lumapps/user/types';

export enum Status {
    live = 'LIVE',
    deleted = 'DELETED',
}

/** Minimal concept type to display in rules */
export type AskAiRuleAssociatedConcept = Pick<AskAiConcept, 'id' | 'title'>;

/** A Rule's associated source */
export type AssociatedSource = Pick<AskAiSource, 'id' | 'title'>;

/** Rule answer type */
export enum RuleAnswerType {
    ExternalSources = 'external_sources',
    Custom = 'custom',
    /** Agent with tools and knowledge sources */
    ToolsAgent = 'tools_agent',
}

/** Basic rule answer */
export interface BaseRuleAnswer {
    /** Id of the answer */
    id?: string;
    /** Type of the answer */
    type?: RuleAnswerType;
}

/** External source answer */
export interface RuleAnswerExternalSourcesType extends BaseRuleAnswer {
    /** External sources */
    sources: AssociatedSource[];
    type?: RuleAnswerType.ExternalSources;
}
/** External source answer output object */
export interface RuleAnswerExternalSourcesOutputDTO extends Omit<RuleAnswerExternalSourcesType, 'sources'> {
    /** External sources */
    sourceIds: AssociatedSource['id'][];
}

export interface MicroAppResponse {
    id: string;
    icon: TranslatableObject;
    name: TranslatableObject;
    description: TranslatableObject;
}

export interface MicroAppActionableItemResponse {
    type: ACTIONABLE_ITEM_TYPES.MICRO_APP;
    payload: MicroAppResponse;
}

export interface UserCardActionableItemResponse {
    type: ACTIONABLE_ITEM_TYPES.USER_CARD;
    payload: User;
}

/** response received from backend when editing rule.
 * Payload is polymorph and change based on the actionable item type */
export type ActionableItemResponse = UserCardActionableItemResponse | MicroAppActionableItemResponse;

export type FormActionableItem = ActionableItemResponse & {
    selectedPicker?: ACTIONABLE_ITEM_TYPES;
};

/** Custom answer */
export interface RuleAnswerCustomType extends BaseRuleAnswer {
    /** Answer message */
    message?: TranslatableObject;
    /** Answer actionable item */
    actionableItem?: ActionableItemResponse;
    type?: RuleAnswerType.Custom;
}

/** Custom answer output object */
export type RuleAnswerCustomTypeOutputDTO = Pick<RuleAnswerCustomType, 'message' | 'type'> & {
    actionableItem?: ActionableItemPayload;
};

/** Custom answer without the "type" attribute. */
export type RuleAnswerCustomWithoutType = Omit<RuleAnswerCustomType, 'type'>;

/** A reference to an agent tool (id and name) */
export type AssociatedTool = Pick<AgentTool, 'id' | 'name'>;

/** Tools agent answer - agent with tools and knowledge sources */
export interface RuleAnswerToolsAgentType extends BaseRuleAnswer {
    /** Tools the agent can use */
    tools?: AssociatedTool[];
    /** External sources for knowledge */
    sources?: AssociatedSource[];
    type?: RuleAnswerType.ToolsAgent;
}

/** Tools agent answer output object */
export interface RuleAnswerToolsAgentOutputDTO extends Omit<RuleAnswerToolsAgentType, 'tools' | 'sources'> {
    /** Tool IDs */
    toolIds?: AssociatedTool['id'][];
    /** Source IDs */
    sourceIds?: AssociatedSource['id'][];
}

/** Rule answer type */
export type RuleAnswer = RuleAnswerCustomType | RuleAnswerExternalSourcesType | RuleAnswerToolsAgentType;

/** Rule answer output DTO */
export type RuleAnswerOutputDTO = Omit<
    RuleAnswerCustomType | RuleAnswerExternalSourcesOutputDTO | RuleAnswerToolsAgentOutputDTO,
    'id'
>;

/** Ask AI rule */
export interface AskAiRule {
    /** Id of the rule */
    id: string;
    /** Creation date of the rule */
    createdAt: string;
    /** Last update date of the rule */
    updatedAt?: string;
    /** User who created the rule */
    createdBy: MinimumViableUser<'id' | 'fullName'>;
    /** User who updated the rule */
    updatedBy?: MinimumViableUser<'id' | 'fullName'>;
    /** Title of the rule */
    title: string;
    /** Description of the rule */
    description?: string;
    /** Status of the rule */
    status: Status;
    /** Application group of the rule */
    groups: Pick<Group, 'name' | 'id' | 'status'>[];
    /** Associated sources of the rule */
    sources: AssociatedSource[];
    /** Associated concepts of the rule (to be matched with the concepts in the ruleQuery field) */
    concepts: AskAiRuleAssociatedConcept[];
    /** Query of the rule (concepts structure) */
    query: {
        or: { concepts: AskAiRuleAssociatedConcept['id'][] }[];
    };
    /** Answer of the rule */
    answer: RuleAnswer;
}

/** PUT rule object */
export interface RulePutDTO
    extends Omit<
        AskAiRule,
        'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'groups' | 'answer' | 'sources' | 'concepts' | 'status'
    > {
    /** rule groups override (group IDs only) */
    groups: Group['id'][];
    answer: RuleAnswerOutputDTO;
}

/** POST rule object */
export type RulePostDTO = Omit<RulePutDTO, 'id'>;

export interface CreateOrUpdateRuleDTO extends RulePostDTO {
    id?: string;
}

/** GET rules object */
export interface RulesListDTO {
    /** Current page cursor */
    cursor: string;
    /** Does it have another page */
    more: boolean;
    /** Rules list */
    items: AskAiRule[];
}

/** Sort keys */
export enum SortKeys {
    AZ_TITLE = 'title',
    ZA_TITLE = '-title',
    MOST_RECENT = 'updatedAt',
    LEAST_RECENT = '-updatedAt',
}

/** Generic list params object */
export interface ListParamsDTO {
    /** Statuses to retrieve */
    status?: Status[];
    /** Maximum count of result per page */
    maxResults?: number;
    /** Current page cursor */
    cursor?: string;
    /** How to sort the results */
    sort?: SortKeys;
    /** Filter by title */
    title?: string;
}

/** GET rules params object */
export type ListRulesParamsDTO = ListParamsDTO;

export type AskAiRuleFormState = Partial<RulePutDTO>;

/** Values managed by the RuleCustomMessageDialog */
export type CustomMessageFormDialogValues = Omit<RuleAnswerCustomType, 'message'> & {
    lang: string;
    /**
     * As the backend gives us markdown but the editor uses "wrex nodes"
     * we need to convert what the "message" actually is in the context of the form
     */
    message: TranslatableObject<AIMessageRichTextValue>;
    actionableItem: FormActionableItem;
};

/** actionable item as list item */
export interface ActionableItemListItem {
    label: string;
    icon: string;
    value: ACTIONABLE_ITEM_TYPES;
}

/** For displaying microApp in DefaultPreview */
export interface MicroAppPreview {
    /** id of the microapp */
    id: string;
    /** Link image URLs */
    images?: string[];
    /** Index of the image to use as thumbnail (from the `images` array) */
    thumbnailIndex?: number;
    /** Link description */
    description?: string;
    /** Link title */
    title?: string;
}

/** Concepts select field type in the rules creation/edition form */
export interface ConceptPickerField {
    id: string;
    concepts: AskAiConcept[];
}

/** Array of concepts ids and titles sorted by condition groups */
export type FormattedConceptsToFieldArray = Record<'concepts', AskAiRuleAssociatedConcept[]>[];

/** Payload sent by the rule creation/edition form */
export interface AskAiRuleFormOutput extends Omit<CreateOrUpdateRuleDTO, 'concepts' | 'groups' | 'answer'> {
    concepts: Array<Record<'concepts', AskAiConcept[]>>;
    groups: AskAiRule['groups'];
    answer: AskAiRule['answer'];
}

export interface AskAiRuleFormSubSectionProps {
    isDisabled: boolean;
}
